/**
 * TOSS Web Application — what the signed-in customer may do in their own company
 *
 * The portal's answer to `usePermission`, and deliberately a different mechanism.
 *
 * `permissionStore` resolves to an EMPTY set for a client session on purpose (decision Р31: the
 * portal gates on role at the route level and never on internal keys), and `isNavItemVisible`
 * filters out any menu item that names one — so a keyed item would vanish from the customer's menu
 * entirely. That branch is load-bearing and stays untouched. This hook is the second source, for
 * the portal's OWN keys.
 *
 * The data rides on `GET /api/client/users/me`, which the portal already re-reads at the two
 * moments the answer can change: page load, and switching legal entity. A separate `my-keys`
 * endpoint would only be a second thing to remember to refetch — and forgetting it would show
 * someone a section their next request is refused from.
 *
 * **Hiding, never protecting.** Every write is gated server-side by `[RequireClientCapability]`.
 */

import { useMemo } from 'react';
import { useClientProfile } from '@/hooks/api/clientPortal/useClientProfile';
import { CLIENT_ROLES, type ClientPermissionKey, type ClientRole } from '@/constants/clientPermissions';

// ============================================================================
// Types
// ============================================================================

export interface ClientCapabilities {
  /** The caller's effective keys in the ACTIVE company. Empty until the profile arrives. */
  keys: ReadonlySet<string>;
  /** Their role in that company. */
  role: ClientRole;
  /** Its Russian name, as the server spells it. */
  roleName: string;
  /** Whether they own the cabinet — `can()` is always true for them. */
  isPortalOwner: boolean;
  /**
   * Whether the answer is known yet.
   *
   * Guards must show a loading state while this is false rather than redirecting: a direct link
   * opened in a fresh tab arrives before the profile does, and redirecting on "not yet" sends
   * people to the dashboard for no reason.
   */
  isLoaded: boolean;
  /** Whether the profile request failed — the caller decides whether that is fatal. */
  isError: boolean;
  can: (key: ClientPermissionKey) => boolean;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * @example
 * ```tsx
 * const caps = useClientCapabilities();
 * {caps.can(CPK.Employees.Manage) && <Button>Добавить сотрудника</Button>}
 * ```
 */
export function useClientCapabilities(): ClientCapabilities {
  const query = useClientProfile();

  return useMemo(() => {
    const profile = query.data;
    const keys = new Set<string>(profile?.permissions ?? []);
    const isPortalOwner = profile?.isPortalOwner ?? false;

    return {
      keys,
      // Observer while nothing is known — the narrowest role there is. Under-granting shows a
      // customer less than they have for a moment; over-granting offers actions the server refuses.
      role: (profile?.clientRole as ClientRole | undefined) ?? CLIENT_ROLES.Observer,
      roleName: profile?.roleName ?? '',
      isPortalOwner,
      isLoaded: profile !== undefined,
      isError: query.isError,
      // The owner holds everything by computation on the server too, so this mirrors rather than
      // decides. Without it an owner who unticked their own role's boxes would lose the screen that
      // unticks them.
      can: (key: ClientPermissionKey) => isPortalOwner || keys.has(key),
    };
  }, [query.data, query.isError]);
}

/**
 * Whether an error is the server refusing THIS action rather than the session being dead.
 *
 * The distinction matters: a 403 on a client route must never be treated as a dead session. That is
 * the trap already fixed once for the change-password screen, where a wrong current password logged
 * the customer out of every tab. A refused capability re-reads the profile and shows a message.
 */
export function isClientPermissionDenied(error: unknown): boolean {
  const code = (error as { response?: { data?: { errorCode?: string } } })?.response?.data?.errorCode;
  return code === 'CLIENT_PERMISSION_DENIED';
}
